"use client";

import * as React from "react";
import { cn } from "../lib/cn";
import { useReducedMotion } from "../hooks/useReducedMotion";

/**
 * FlickeringGrid — canvas-driven field of softly flickering squares for hero
 * and section backgrounds. Ported from Creator Studio v2
 * (`src/components/ui/flickering-grid.tsx`) and re-themed: the default colour
 * reads the consumer's `--accent` token instead of hardcoded black, so the
 * grid re-tints per studio and in dark mode without prop changes.
 *
 * Colour resolution: `color` accepts any CSS colour. Strings containing a
 * `var(--token)` reference are resolved against the container's computed
 * style at mount (and whenever `color` changes), then rasterised once for the
 * canvas. A mid-session theme flip re-resolves on the next `color` /
 * dimension prop change, not automatically.
 *
 * Performance: one `requestAnimationFrame` loop drives the whole grid; an
 * IntersectionObserver pauses it entirely while off-screen and a
 * ResizeObserver re-seeds the grid on container resize.
 *
 * Honours `prefers-reduced-motion`: renders a single static frame (the
 * texture stays, the strobe goes).
 *
 * SSR-safe: all `window` / `document` access lives inside `useEffect`.
 *
 * @example
 *   <div className="relative h-64 overflow-hidden rounded-2xl">
 *     <FlickeringGrid className="absolute inset-0" maxOpacity={0.2} />
 *     <h2 className="relative">Build your first AI Studio</h2>
 *   </div>
 */
export interface FlickeringGridProps extends React.HTMLAttributes<HTMLDivElement> {
  /** Square edge length, px. Default 4. */
  squareSize?: number;
  /** Gap between squares, px. Default 6. */
  gridGap?: number;
  /** Per-square chance (0 to 1) of re-rolling opacity each second. Default 0.3. */
  flickerChance?: number;
  /**
   * Square colour. Any CSS colour; `var(--token)` references resolve against
   * the container so the default follows the consumer's theme.
   * Default `hsl(var(--accent))`.
   */
  color?: string;
  /** Fixed canvas width, px. Defaults to the container's width. */
  width?: number;
  /** Fixed canvas height, px. Defaults to the container's height. */
  height?: number;
  /** Peak square opacity (0 to 1). Default 0.3. */
  maxOpacity?: number;
  /**
   * Opt-in image mask URL. When set, the flicker is composited through the
   * image's alpha (`destination-in`) so squares show ONLY inside the opaque
   * regions of the image — e.g. the AISquare logo mark. Off-screen until the
   * image loads (no unmasked flash). SSR-safe; honours reduced motion
   * (static masked frame).
   */
  maskImage?: string;
  /** How the mask image fits the canvas, CSS-style. Default `"contain"`. */
  maskSize?: "contain" | "cover";
  /**
   * Mask anchor, CSS `background-position` syntax — keywords
   * (`left`/`right`/`top`/`bottom`/`center`) or percentages (`"100% 50%"`).
   * Combine two off-edge-anchored instances to reproduce a dual-panel
   * logo bleed. Default `"center"`.
   */
  maskPosition?: string;
}

/** Map a CSS background-position string to fractional [x, y] in 0..1. */
const parseMaskPosition = (pos: string): [number, number] => {
  const kw: Record<string, [number | null, number | null]> = {
    left: [0, null],
    right: [1, null],
    top: [null, 0],
    bottom: [null, 1],
    center: [0.5, 0.5],
  };
  let x = 0.5;
  let y = 0.5;
  let numericSeen = 0;
  pos
    .trim()
    .toLowerCase()
    .split(/\s+/)
    .forEach((t) => {
      if (t.endsWith("%")) {
        const f = Number(t.slice(0, -1)) / 100;
        // CSS order: first numeric token is X, second is Y.
        if (numericSeen === 0) x = f;
        else y = f;
        numericSeen++;
        return;
      }
      const [kx, ky] = kw[t] ?? [null, null];
      if (kx !== null) x = kx;
      if (ky !== null) y = ky;
    });
  return [x, y];
};

/** Resolve one `var(--token)` reference in a colour string against an element. */
const resolveCssColor = (raw: string, el: HTMLElement): string => {
  const varMatch = raw.match(/var\((--[\w-]+)\)/);
  if (!varMatch) return raw;
  const value = getComputedStyle(el).getPropertyValue(varMatch[1]!).trim();
  // Unresolvable token: fall back to a neutral grey rather than transparent.
  if (!value) return "hsl(0 0% 64%)";
  return raw.replace(varMatch[0], value);
};

export const FlickeringGrid = React.forwardRef<HTMLDivElement, FlickeringGridProps>(
  (
    {
      squareSize = 4,
      gridGap = 6,
      flickerChance = 0.3,
      color = "hsl(var(--accent))",
      width,
      height,
      maxOpacity = 0.3,
      maskImage,
      maskSize = "contain",
      maskPosition = "center",
      className,
      ...props
    },
    ref,
  ) => {
    const containerRef = React.useRef<HTMLDivElement>(null);
    const canvasRef = React.useRef<HTMLCanvasElement>(null);
    const reducedMotion = useReducedMotion();

    React.useImperativeHandle(ref, () => containerRef.current as HTMLDivElement);

    React.useEffect(() => {
      const canvas = canvasRef.current;
      const container = containerRef.current;
      if (!canvas || !container) return;
      const ctx = canvas.getContext("2d");
      if (!ctx) return;

      // Rasterise the (possibly token-driven) colour into an rgba( prefix the
      // draw loop can append per-square opacity to.
      const probe = document.createElement("canvas");
      probe.width = probe.height = 1;
      const probeCtx = probe.getContext("2d");
      let rgbaPrefix = "rgba(0, 0, 0,";
      if (probeCtx) {
        probeCtx.fillStyle = resolveCssColor(color, container);
        probeCtx.fillRect(0, 0, 1, 1);
        const [r, g, b] = Array.from(probeCtx.getImageData(0, 0, 1, 1).data);
        rgbaPrefix = `rgba(${r}, ${g}, ${b},`;
      }

      let rafId = 0;
      let cols = 0;
      let rows = 0;
      let dpr = 1;
      let squares = new Float32Array(0);
      let lastTime = 0;
      let inView = true;

      // Optional mask: load the image once; until it's ready we draw nothing
      // (avoids an unmasked flash). The draw loop composites it every frame.
      let maskImg: HTMLImageElement | undefined;
      let maskReady = false;
      if (maskImage) {
        // No crossOrigin: we never read canvas pixels back after compositing,
        // so a tainted canvas is harmless — and forcing CORS would break
        // same-origin logos served without CORS headers.
        maskImg = new Image();
        maskImg.onload = () => {
          maskReady = true;
          draw();
        };
        maskImg.src = maskImage;
      }

      const setup = () => {
        const w = width || container.clientWidth;
        const h = height || container.clientHeight;
        dpr = window.devicePixelRatio || 1;
        canvas.width = w * dpr;
        canvas.height = h * dpr;
        canvas.style.width = `${w}px`;
        canvas.style.height = `${h}px`;
        cols = Math.floor(w / (squareSize + gridGap));
        rows = Math.floor(h / (squareSize + gridGap));
        squares = new Float32Array(cols * rows);
        for (let i = 0; i < squares.length; i++) {
          squares[i] = Math.random() * maxOpacity;
        }
      };

      const draw = () => {
        ctx.clearRect(0, 0, canvas.width, canvas.height);
        // A mask was requested but hasn't loaded yet: stay blank rather than
        // flash the full unmasked grid.
        if (maskImg && !maskReady) return;
        for (let i = 0; i < cols; i++) {
          for (let j = 0; j < rows; j++) {
            ctx.fillStyle = `${rgbaPrefix}${squares[i * rows + j]})`;
            ctx.fillRect(
              i * (squareSize + gridGap) * dpr,
              j * (squareSize + gridGap) * dpr,
              squareSize * dpr,
              squareSize * dpr,
            );
          }
        }
        // Composite the flicker through the mask's alpha: keep pixels only
        // where the image is opaque (CSS-style contain/cover + position).
        if (maskImg && maskReady) {
          const W = canvas.width;
          const H = canvas.height;
          const ar = maskImg.naturalWidth / maskImg.naturalHeight;
          let dw: number;
          let dh: number;
          if (maskSize === "cover" ? W / H > ar : W / H < ar) {
            dw = W;
            dh = W / ar;
          } else {
            dh = H;
            dw = H * ar;
          }
          const [px, py] = parseMaskPosition(maskPosition);
          ctx.save();
          ctx.globalCompositeOperation = "destination-in";
          ctx.drawImage(maskImg, (W - dw) * px, (H - dh) * py, dw, dh);
          ctx.restore();
        }
      };

      const tick = (time: number) => {
        const dt = lastTime ? (time - lastTime) / 1000 : 0;
        lastTime = time;
        for (let i = 0; i < squares.length; i++) {
          if (Math.random() < flickerChance * dt) {
            squares[i] = Math.random() * maxOpacity;
          }
        }
        draw();
        rafId = requestAnimationFrame(tick);
      };

      setup();
      draw(); // Static first frame — all reduced-motion users ever see.

      const resizeObserver = new ResizeObserver(() => {
        setup();
        draw();
      });
      resizeObserver.observe(container);

      let intersectionObserver: IntersectionObserver | undefined;
      if (!reducedMotion) {
        intersectionObserver = new IntersectionObserver(
          ([entry]) => {
            const nowInView = entry?.isIntersecting ?? false;
            if (nowInView && !inView) {
              lastTime = 0;
              rafId = requestAnimationFrame(tick);
            } else if (!nowInView && inView) {
              cancelAnimationFrame(rafId);
            }
            inView = nowInView;
          },
          { threshold: 0 },
        );
        intersectionObserver.observe(canvas);
        rafId = requestAnimationFrame(tick);
      }

      return () => {
        cancelAnimationFrame(rafId);
        resizeObserver.disconnect();
        intersectionObserver?.disconnect();
      };
    }, [
      color,
      width,
      height,
      squareSize,
      gridGap,
      maxOpacity,
      flickerChance,
      reducedMotion,
      maskImage,
      maskSize,
      maskPosition,
    ]);

    return (
      <div
        ref={containerRef}
        aria-hidden="true"
        className={cn("h-full w-full", className)}
        {...props}
      >
        <canvas ref={canvasRef} className="pointer-events-none" />
      </div>
    );
  },
);
FlickeringGrid.displayName = "FlickeringGrid";
